import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, DatabaseZap, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDate } from "../lib/roles";
import { Loading, PageHeader, Panel } from "../components/common";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { SwitchPanel } from "../components/SwitchPanel";

const LABEL = { running: "In corso", done: "Completata", error: "Interrotta" };

const AccessInfo = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api("/web/access/status").then(setS).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <Panel className="mb-6 space-y-2" data-testid="access-info">
      <h2 className="font-display font-bold text-[var(--navy)]">Copia notturna e accessi</h2>
      <p className="text-sm text-slate-600" data-testid="nightly-info">Ogni notte alle 03:00 la copia si ripete da sola. {s.nightly ? `Usa l'accesso di ${s.nightly.user_name}, valido fino al ${fmtDate(s.nightly.valid_until)}: basta che un Direttore entri nella web app almeno una volta al mese.` : "Per attivarla, un Direttore deve entrare nella web app."}</p>
      <p className="text-sm text-slate-600" data-testid="credentials-info">Password già pronte per il passaggio: <b>{s.with_password}</b> su {s.users} dipendenti. Si salvano da sole quando ciascuno entra nella web app.</p>
    </Panel>
  );
};

const Counts = ({ counts }) => {
  const rows = Object.entries(counts || {}).filter(([, n]) => n > 0).sort(([a], [b]) => a.localeCompare(b));
  if (!rows.length) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm mt-4" data-testid="import-counts">{rows.map(([k, n]) => (
      <div key={k} className="flex justify-between border-b border-dashed py-1"><span className="text-slate-600">{k.replace(/^m_/, "").replace(/_/g, " ")}</span><b className="text-[var(--navy)]">{n}</b></div>))}
    </div>
  );
};

const RunPanel = ({ run }) => {
  const pct = Math.round((100 * (run.done_steps || 0)) / Math.max(1, run.total_steps || 1));
  return (
    <Panel data-testid="import-run">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {run.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : run.status === "error" ? <TriangleAlert className="w-5 h-5 text-[var(--red)]" /> : <DatabaseZap className="w-5 h-5 text-[var(--teal)] animate-pulse" />}
        <span className="font-semibold text-[var(--navy)]" data-testid="import-status">{LABEL[run.status]}</span>
        <span className="text-xs text-slate-500">Avviata da {run.started_by} il {fmtDate(run.started_at, true)}{run.finished_at ? ` · finita il ${fmtDate(run.finished_at, true)}` : ""}</span>
      </div>
      <Progress value={pct} className="h-2" data-testid="import-progress" />
      <p className="text-xs text-slate-500 mt-2">{pct}% · {run.step}</p>
      <Counts counts={run.counts} />
      {(run.errors || []).length > 0 && (
        <details className="mt-4 text-sm" data-testid="import-errors"><summary className="cursor-pointer text-[var(--red)]">{run.errors.length} elementi non letti</summary>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">{run.errors.map((e, i) => <li key={i}>{e}</li>)}</ul></details>)}
    </Panel>
  );
};

export default function AdminImport() {
  const [run, setRun] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("/web/import/status").then((d) => setRun(d.run || null)).catch(() => setRun(null)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (run?.status !== "running") return undefined;
    const id = setInterval(load, 2500);
    return () => clearInterval(id);
  }, [run?.status, load]);
  const start = async () => {
    if (!window.confirm("Avviare la copia di tutti i dati dall'app mobile? I dati copiati in precedenza verranno sostituiti.")) return;
    setBusy(true);
    try { setRun(await api("/web/import/start", { method: "POST" })); toast.success("Importazione avviata"); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (run === undefined) return <Loading />;
  return (
    <div className="max-w-4xl">
      <PageHeader subtitle="Passaggio alla web app" title="Importa dati dall'app mobile" />
      <SwitchPanel />
      <Panel className="mb-6 space-y-3">
        <p className="text-sm text-slate-600">Copia nel database della web app tutti i dati che il server dell'app mobile mostra al Direttore: personale, turni, ferie, report, Vigilanza, avvisi, compiti, mappa del porto, manutenzioni, contatori, corsi e brevetti con gli allegati.</p>
        <p className="text-sm text-slate-600">Per ora la web app continua a usare i dati dell'app mobile: questa copia serve a preparare il passaggio e si può ripetere quando vuoi. Le password, i post-it e le notifiche personali dei dipendenti non si possono copiare.</p>
        <Button onClick={start} disabled={busy || run?.status === "running"} className="rounded-full bg-[var(--teal)]" data-testid="import-start-button"><DatabaseZap className="w-4 h-4 mr-2" />{run?.status === "running" ? "Importazione in corso..." : "Importa dati dall'app mobile"}</Button>
      </Panel>
      <AccessInfo />
      {run ? <RunPanel run={run} /> : <p className="text-sm text-slate-500" data-testid="import-never">Nessuna importazione eseguita finora.</p>}
    </div>
  );
}
